import { FetchPolicy } from "@apollo/client";
import { useApolloClient, useLazyQuery } from "@apollo/client/react";
import { useState } from "react";

import { useAsync } from "@smart/itops-hooks-dom";
import { isNotNullOrUndefined } from "@smart/itops-utils-basic";
import { Gql, queryDocuments } from "@smart/manage-gql-operations-dom";

import { useQueryFactory } from "./base";

export const useLoadMatterFields = useQueryFactory(
  queryDocuments.matterFields,
  {
    map: ({ matterFields }) => matterFields,
  },
);

export const useQueryMatterFields = (options?: {
  fetchPolicy?: FetchPolicy;
}) => {
  const apollo = useApolloClient();

  return async (variables: Gql.MatterFieldsQueryVariables) => {
    if (!apollo) return [];

    const result = await apollo.query({
      query: queryDocuments.matterFields,
      variables,
      fetchPolicy: options?.fetchPolicy,
    });
    return result.data?.matterFields || [];
  };
};

export const useLoadMatterLayouts = useQueryFactory(
  queryDocuments.matterLayouts,
  {
    map: ({ matterLayouts }) => matterLayouts,
  },
);

export const useQueryMatterLayouts = () => {
  const apollo = useApolloClient();

  return async (variables: Gql.MatterLayoutsQueryVariables) => {
    if (!apollo) return [];

    const result = await apollo.query({
      query: queryDocuments.matterLayouts,
      variables,
    });
    return result.data?.matterLayouts || [];
  };
};

export const useLoadMatterTypeCategories = useQueryFactory(
  queryDocuments.matterTypeCategories,
  {
    map: ({ matterTypeCategories }) =>
      matterTypeCategories?.map((item) => item?.trim()),
  },
);

export const useLoadMatterTypeSearch = useQueryFactory(
  queryDocuments.matterTypeSearch,
  {
    map: ({ matterTypeSearch }) => matterTypeSearch,
  },
);

export const useQueryMatterTypeSearch = () => {
  const apollo = useApolloClient();

  return async (variables: Gql.MatterTypeSearchQueryVariables) => {
    if (!apollo) return [];

    const result = await apollo.query({
      query: queryDocuments.matterTypeSearch,
      variables,
    });
    return result.data?.matterTypeSearch || [];
  };
};

export const useLoadMatterType = useQueryFactory(queryDocuments.matterType, {
  map: ({ matterType }) => matterType,
});

export const useLoadMatterTypes = useQueryFactory(queryDocuments.matterTypes, {
  map: ({ matterTypes }) => matterTypes,
});

export const useQueryMatterTypes = (variables: {
  type: "lead" | "matter";
  locations: string[];
  categories: string[];
}) => {
  const apollo = useApolloClient();
  const [byCategory, setByCategory] = useState<
    {
      category: string;
      loading: boolean;
      matterTypes: {
        name: string;
        category: string;
        matterTypes: NonNullable<Gql.MatterTypeQuery["matterType"]>[];
      }[];
    }[]
  >([]);

  useAsync(
    async (info) => {
      if (!apollo) return;

      setByCategory(
        variables.categories.map((category) => ({
          category,
          loading: true,
          matterTypes: [],
        })),
      );

      variables.categories.forEach(async (category) => {
        const byLocation = await Promise.all(
          variables.locations.map(async (location) => {
            const result = await apollo.query({
              query: queryDocuments.matterTypes,
              variables: { type: variables.type, location, category },
            });
            return result.data?.matterTypes;
          }),
        );

        const categoryMatterTypes = (byLocation?.[0] || [])
          .map((item) => {
            const matterTypes = byLocation
              .map((loc) => loc?.find((m) => m.name === item.name))
              .filter(isNotNullOrUndefined);
            return matterTypes.length === byLocation.length
              ? { name: item.name, category, matterTypes }
              : undefined;
          })
          .filter(isNotNullOrUndefined);

        if (info.mounted)
          setByCategory((prev) =>
            prev.map((prevItem) =>
              prevItem.category === category
                ? {
                    category,
                    loading: false,
                    matterTypes: categoryMatterTypes,
                  }
                : prevItem,
            ),
          );
      });
    },
    [
      apollo,
      variables.type,
      variables.locations.join(","),
      variables.categories.join(","),
    ],
  );

  return {
    byCategory,
  };
};

export const useLazyLoadMatterTypes = () => {
  const client = useApolloClient();
  return useLazyQuery(queryDocuments.matterTypes, { client });
};

export const useQueryMatterLayoutFields = () => {
  const apollo = useApolloClient();

  return async (variables: Gql.MatterLayoutFieldsQueryVariables) => {
    if (!apollo) return [];

    const result = await apollo.query({
      query: queryDocuments.matterLayoutFields,
      variables,
    });
    return result.data?.matterLayoutFields || [];
  };
};
